import React, { MouseEvent } from 'react';
import styled from 'styled-components';

import { DiagramEngine, LinkWidget, PointModel } from '@projectstorm/react-diagrams-core';
import { Point } from '@projectstorm/geometry';
import { DefaultLinkSegmentWidget } from '@projectstorm/react-diagrams-defaults';

import { PipeLinkFactory, PipeLinkModel } from './PipeLink';
import { getDeviceToRender } from '../components/PIDEditing/PIDEditing.utils';

const AddPointCircle = styled.circle`
  fill: #22bbe8;
  stroke: #22bbe8;
  stroke-width: 8;
  cursor: pointer;
  pointer-events: all;
  &:hover {
    fill: rgb(29, 95, 115);
  }
  z-index: 2;
`;
const ClickableArea = styled.circle`
  fill: transparent;
  stroke: none;
  cursor: pointer;
  pointer-events: all;
  z-index: 1;
`;

interface ThemeToken {
  colorIcon: string;
  [key: string]: unknown;
}

export interface PipeLinkProps {
  color?: string;
  width?: number;
  smooth?: boolean;
  link: PipeLinkModel;
  diagramEngine: DiagramEngine;
  factory: PipeLinkFactory;
  editMode?: boolean;
  token?: ThemeToken;
}

export interface PipeLinkState {
  selected: boolean;
  canDrag: boolean;
  editMode: boolean;
  clickedPosition?: { x: number; y: number } | null;
  hasOverlappingPipes?: boolean;
  closestSegmentIndex?: number;
}

export class PipeLinkWidget extends React.Component<PipeLinkProps, PipeLinkState> {
  public static defaultProps: PipeLinkProps = {
    width: 3,
    link: null,
    smooth: false,
    diagramEngine: null,
    factory: null,
    editMode: false,
    token: null,
  };

  refPaths: React.RefObject<SVGPathElement>[];
  unsubscribe: (() => void) | null = null;
  unsubscribeClickedPipe: (() => void) | null = null;
  unsubscribeDeviceTypeChange: (() => void) | null = null;

  // DOM references to the label and paths (if label is given), used to calculate dynamic positioning
  refLabels: { [id: string]: HTMLElement };
  dragging_index: number;

  constructor(props: PipeLinkProps) {
    super(props);
    this.refPaths = [];
    this.state = {
      selected: false,
      canDrag: false,
      editMode: props.editMode || false,
      clickedPosition: null,
      hasOverlappingPipes: false,
    };

    this.dragging_index = 0;
  }

  componentDidMount() {
    this.unsubscribe = this.props.factory.subscribeToStateChange(isEditMode => {
      this.setState({ editMode: isEditMode });
    });

    this.unsubscribeClickedPipe = this.props.factory.subscribeToClickedPipe(() => {
      this.forceUpdate();
    });

    this.unsubscribeDeviceTypeChange = this.props.factory.subscribeToDeviceTypeChange(() => {
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.unsubscribeClickedPipe) {
      this.unsubscribeClickedPipe();
    }
    if (this.unsubscribeDeviceTypeChange) {
      this.unsubscribeDeviceTypeChange();
    }
  }

  componentDidUpdate(prevProps: PipeLinkProps) {
    // Update rendered paths
    this.props.link.setRenderedPaths(this.refPaths.map(ref => ref.current));

    if (prevProps.editMode !== this.props.editMode) {
      this.setState({ editMode: this.props.editMode });
    }
  }

  // Add method to detect overlapping pipes
  detectOverlappingPipes(clickedPoint: { x: number; y: number }, tolerance = 10): PipeLinkModel[] {
    const overlappingLinks: PipeLinkModel[] = [];
    const engine = this.props.diagramEngine;
    const allLinks = engine.getModel().getLinks();

    Object.values(allLinks).forEach(link => {
      if (link.getID() !== this.props.link.getID() && link instanceof PipeLinkModel) {
        const points = link.getPoints();

        // Check each segment of the link
        for (let i = 0; i < points.length - 1; i++) {
          const point1 = points[i];
          const point2 = points[i + 1];

          // Check if the clicked point is on this segment
          if (this.isPointOnSegment(clickedPoint, point1.getPosition(), point2.getPosition(), tolerance)) {
            overlappingLinks.push(link);
            break; // Found overlap on this link, no need to check other segments
          }
        }
      }
    });

    return overlappingLinks;
  }

  // Helper method to check if a point lies on a line segment
  isPointOnSegment(
    point: { x: number; y: number },
    segmentStart: { x: number; y: number },
    segmentEnd: { x: number; y: number },
    tolerance: number,
  ): boolean {
    // Calculate distance from point to line segment
    const A = point.x - segmentStart.x;
    const B = point.y - segmentStart.y;
    const C = segmentEnd.x - segmentStart.x;
    const D = segmentEnd.y - segmentStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
      // Segment is a point
      return Math.sqrt(A * A + B * B) <= tolerance;
    }

    const param = dot / lenSq;

    let xx, yy;
    if (param < 0) {
      xx = segmentStart.x;
      yy = segmentStart.y;
    } else if (param > 1) {
      xx = segmentEnd.x;
      yy = segmentEnd.y;
    } else {
      xx = segmentStart.x + param * C;
      yy = segmentStart.y + param * D;
    }

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy) <= tolerance;
  }

  generateLink(path: string, extraProps: Record<string, unknown>, id: string | number): JSX.Element {
    const ref = React.createRef<SVGPathElement>();
    this.refPaths.push(ref);
    return (
      <DefaultLinkSegmentWidget
        key={`link-${id}`}
        path={path}
        selected={this.state.selected}
        diagramEngine={this.props.diagramEngine}
        factory={this.props.diagramEngine.getFactoryForLink(this.props.link)}
        link={this.props.link}
        forwardRef={ref}
        onSelection={selected => {
          this.setState({ selected: selected });
        }}
        extras={extraProps}
      />
    );
  }

  calculatePositions(points: PointModel[], event: MouseEvent, index: number, coordinate: string) {
    // If path is first or last add another point to keep node port on its position
    if (index === 0) {
      const point = new PointModel({
        link: this.props.link,
        position: new Point(points[index].getX(), points[index].getY()),
      });
      this.props.link.addPoint(point, index);
      this.dragging_index++;
      return;
    } else if (index === points.length - 2) {
      const point = new PointModel({
        link: this.props.link,
        position: new Point(points[index + 1].getX(), points[index + 1].getY()),
      });
      this.props.link.addPoint(point, index + 1);
      return;
    }

    // Merge two points if it is not close to node port and close to each other
    if (index - 2 > 0) {
      const _points = {
        [index - 2]: points[index - 2].getPosition(),
        [index + 1]: points[index + 1].getPosition(),
        [index - 1]: points[index - 1].getPosition(),
      };
      if (Math.abs(_points[index - 1][coordinate] - _points[index + 1][coordinate]) < 5) {
        _points[index - 2][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
        _points[index + 1][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
        points[index - 2].setPosition(_points[index - 2]);
        points[index + 1].setPosition(_points[index + 1]);
        points[index - 1].remove();
        points[index - 1].remove();
        this.dragging_index--;
        this.dragging_index--;
        return;
      }
    }

    // Merge two points if it is not close to node port
    if (index + 2 < points.length - 2) {
      const _points = {
        [index + 3]: points[index + 3].getPosition(),
        [index + 2]: points[index + 2].getPosition(),
        [index + 1]: points[index + 1].getPosition(),
        [index]: points[index].getPosition(),
      };
      if (Math.abs(_points[index + 1][coordinate] - _points[index + 2][coordinate]) < 5) {
        _points[index][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
        _points[index + 3][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
        points[index].setPosition(_points[index]);
        points[index + 3].setPosition(_points[index + 3]);
        points[index + 1].remove();
        points[index + 1].remove();
        return;
      }
    }

    // If no condition above handled then just update path points position
    const _points = {
      [index]: points[index].getPosition(),
      [index + 1]: points[index + 1].getPosition(),
    };
    _points[index][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
    _points[index + 1][coordinate] = this.props.diagramEngine.getRelativeMousePoint(event)[coordinate];
    points[index].setPosition(_points[index]);
    points[index + 1].setPosition(_points[index + 1]);
  }

  draggingEvent(event: MouseEvent, index: number) {
    const points = this.props.link.getPoints();
    // get moving difference. Index + 1 will work because links indexes has
    // length = points.lenght - 1
    const dx = Math.abs(points[index].getX() - points[index + 1].getX());
    const dy = Math.abs(points[index].getY() - points[index + 1].getY());

    // moving with y direction
    if (dx === 0) {
      this.calculatePositions(points, event, index, 'x');
    } else if (dy === 0) {
      this.calculatePositions(points, event, index, 'y');
    }
    this.props.link.setFirstAndLastPathsDirection();
  }

  handleMove = function (event: MouseEvent) {
    this.draggingEvent(event, this.dragging_index);
  }.bind(this);

  handleUp = function () {
    // Unregister handlers to avoid multiple event handlers for other links
    this.setState({ canDrag: false, selected: false });
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mouseup', this.handleUp);
  }.bind(this);

  G = styled.g`
    path:not(.plus-sign-path) {
      stroke: ${this.props.color};
      stroke-dasharray: none;
      cursor: default;
    }
  `;

  /*
  gets the shortest distance (x-axis) between a parent's link and and all its
  corresponding links (every parent can have one or more links, e.g. a compressor can be linked to 2 different air-filters etc)
  */
  getShortestDx(): number {
    const sourcePort = this.props.link.getSourcePort();
    if (!sourcePort) {
      return 0;
    }
    const sourcePortLinks = sourcePort.getLinks();
    const sourcePortLinksArr = Object.values(sourcePortLinks);
    const dxs = sourcePortLinksArr.map(link => {
      const firstPointX = link.getFirstPoint().getX();
      const lastPointX = link.getLastPoint().getX();
      const dx = Math.abs(firstPointX - lastPointX);
      return dx;
    });
    const shortestDx = Math.min(...dxs);

    return shortestDx;
  }

  handleAddDeviceClick = (e: MouseEvent) => {
    // Get the diagram-cover container (parent of the canvas)
    const diagramCover = e.currentTarget.closest('.diagram-cover') as HTMLElement;
    if (!diagramCover) return;

    // Get the container's bounding rect
    const containerRect = diagramCover.getBoundingClientRect();

    // Calculate position relative to the container
    const screenPosition = {
      x: e.clientX - containerRect.left,
      y: e.clientY - containerRect.top,
    };
    this.props.factory.handleAddDeviceClick(this.props.link.getID(), screenPosition);
  };

  // Helper method to normalize position to the horizontal segment
  normalizePositionToSegment(clickPosition: { x: number; y: number }): { x: number; y: number } {
    const points = this.props.link.getPoints();
    if (points.length < 2) return clickPosition;

    // Find the first horizontal segment (where Y coordinates are the same)
    for (let i = 0; i < points.length - 1; i++) {
      const point1 = points[i].getPosition();
      const point2 = points[i + 1].getPosition();

      // Check if it's a horizontal segment
      if (Math.abs(point1.y - point2.y) < 5) {
        const segmentY = point1.y;
        const minX = Math.min(point1.x, point2.x);
        const maxX = Math.max(point1.x, point2.x);

        // Clamp the X position to the segment bounds
        const clampedX = Math.max(minX, Math.min(maxX, clickPosition.x));

        return { x: clampedX, y: segmentY };
      }
    }

    // Fallback to original position if no horizontal segment found
    return clickPosition;
  }

  // Helper method to find the closest segment to the clicked position
  findClosestSegment(clickPosition: { x: number; y: number }): number {
    const points = this.props.link.getPoints();
    let closestSegmentIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < points.length - 1; i++) {
      const segmentStart = points[i].getPosition();
      const segmentEnd = points[i + 1].getPosition();

      // Calculate distance from click point to this segment
      const distance = this.getDistanceToSegment(clickPosition, segmentStart, segmentEnd);

      if (distance < minDistance) {
        minDistance = distance;
        closestSegmentIndex = i;
      }
    }

    return closestSegmentIndex;
  }

  // Helper method to calculate distance from point to line segment
  getDistanceToSegment(
    point: { x: number; y: number },
    segmentStart: { x: number; y: number },
    segmentEnd: { x: number; y: number },
  ): number {
    const A = point.x - segmentStart.x;
    const B = point.y - segmentStart.y;
    const C = segmentEnd.x - segmentStart.x;
    const D = segmentEnd.y - segmentStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
      return Math.sqrt(A * A + B * B);
    }

    let param = dot / lenSq;
    param = Math.max(0, Math.min(1, param));

    const xx = segmentStart.x + param * C;
    const yy = segmentStart.y + param * D;

    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  render() {
    //ensure id is present for all points on the path
    const points = this.props.link.getPoints();
    const paths = [];

    // Get points based on link orientation
    let pointLeft = points[0];
    let pointRight = points[points.length - 1];
    let hadToSwitch = false;
    if (pointLeft.getX() > pointRight.getX()) {
      pointLeft = points[points.length - 1];
      pointRight = points[0];
      hadToSwitch = true;
    }

    const dx = Math.abs(points[0].getX() - points[points.length - 1].getX());
    const dy = Math.abs(points[0].getY() - points[points.length - 1].getY());
    const shortestDx = this.getShortestDx();

    // When new link add one middle point to get everywhere 90° angle
    if (this.props.link.getTargetPort() === null && points.length === 2) {
      [...Array(2)].forEach(() => {
        this.props.link.addPoint(
          new PointModel({
            link: this.props.link,
            position: new Point(pointLeft.getX(), pointRight.getY()),
          }),
          1,
        );
      });
      this.props.link.setManuallyFirstAndLastPathsDirection(true, true);
    }
    // When new link is moving and not connected to target port move with middle point
    // TODO: @DanielLazarLDAPPS This will be better to update in DragNewLinkState
    //  in function fireMouseMoved to avoid calling this unexpectedly e.g. after Deserialize
    else if (this.props.link.getTargetPort() === null && this.props.link.getSourcePort() !== null) {
      points[1].setPosition(
        pointRight.getX() + (pointLeft.getX() - pointRight.getX()) / 2,
        !hadToSwitch ? pointLeft.getY() : pointRight.getY(),
      );
      points[2].setPosition(
        pointRight.getX() + (pointLeft.getX() - pointRight.getX()) / 2,
        !hadToSwitch ? pointRight.getY() : pointLeft.getY(),
      );
    }
    // Render was called but link is not moved but user.
    // Node is moved and in this case fix coordinates to get 90° angle.
    // For loop just for first and last path
    /*
    zemingo: this function sets position for the 2 added 90°-angle points.
    every 90° angle has 4 points: start, end, and 2 in the middle for curviness.
    basically this function affects the second and third points.
    it also adds a margin between port to the second point (where 90° angle initially takes a turn)
    the margin is third of the total length between the 2 points ( e.g. (shortestDx * 0.75))
    * */
    else if (!this.state.canDrag && points.length > 2) {
      // Those points and its position only will be moved
      for (let i = 1; i < points.length; i += points.length - 2) {
        if (i === 1) {
          if (this.props.link.getFirstPathXdirection()) {
            points[i].setPosition(points[0].getX() + shortestDx * 0.75, points[0].getY());
          } else {
            points[i].setPosition(points[0].getX() + shortestDx * 0.75, points[0].getY());
          }
        } else {
          if (this.props.link.getLastPathXdirection()) {
            points[i - 1].setPosition(points[1].getX(), points[i].getY());
          } else {
            points[i - 1].setPosition(points[i - 2].getX(), points[i].getY());
          }
        }
      }
    }

    // If there is existing link which has two points add two
    // NOTE: It doesn't matter if check is for dy or dx
    if (points.length === 2 && (dx !== 0 || dy !== 0) && !this.state.canDrag) {
      this.props.link.addPoint(
        new PointModel({
          link: this.props.link,
          position: new Point(pointRight.getX(), pointLeft.getY()),
        }),
        1,
      );
      this.props.link.addPoint(
        new PointModel({
          link: this.props.link,
          position: new Point(pointRight.getX(), pointLeft.getY()),
        }),
        2,
      );
    }
    for (let j = 0; j < points.length - 1; j++) {
      paths.push(
        this.generateLink(
          LinkWidget.generateLinePath(points[j], points[j + 1]),
          {
            'data-linkid': this.props.link.getID(),
            'data-point': j,
            onMouseDown: (event: MouseEvent) => {
              if (event.button === 0 && !this.props.link.isLocked()) {
                
                this.setState({ canDrag: true });
                this.dragging_index = j;
                window.addEventListener('mousemove', this.handleMove);
                window.addEventListener('mouseup', this.handleUp);
                
              }

              // Get click position relative to diagram
              const clickPosition = this.props.diagramEngine.getRelativeMousePoint(event);

              // Detect overlapping pipes at this position
              const overlappingLinks = this.detectOverlappingPipes(clickPosition);

              // Always store the clicked position and closest segment
              const normalizedPosition = this.normalizePositionToSegment(clickPosition);
              const closestSegmentIndex = this.findClosestSegment(clickPosition);

              if (overlappingLinks.length > 0) {
                this.props.factory.pidEditStore?.setIsMultipleConnections(true);
                this.props.factory.pidEditStore?.setSelectedTargetDeviceId([this.props.link, ...overlappingLinks]);
                this.setState({
                  clickedPosition: normalizedPosition,
                  hasOverlappingPipes: true,
                  closestSegmentIndex: closestSegmentIndex,
                });
              } else {
                this.props.factory.pidEditStore?.setIsMultipleConnections(false);
                // For single pipe, still store position but mark as non-overlapping
                this.setState({
                  clickedPosition: normalizedPosition,
                  hasOverlappingPipes: false,
                  closestSegmentIndex: closestSegmentIndex,
                });
              }

              this.props.factory.setClickedPipe(this.props.link.getID());
            },

            onMouseEnter: () => {
              if (!this.props.link.isLocked()) {
                this.setState({ selected: true });
                this.props.link.lastHoverIndexOfPath = j;
              }
            },
          },
          j,
        ),
      );
    }

    // Add circle with plus sign for the first horizontal segment in edit mode
    if (this.state.editMode && points.length > 2 && this.props.factory.clickedPipeId === this.props.link.getID()) {
      // Use clicked position and closest segment when available
      let midX, midY;

      if (this.state.clickedPosition && this.state.closestSegmentIndex !== undefined) {
        // For both overlapping and single pipes, use the closest segment to the click
        const segmentIndex = this.state.closestSegmentIndex;

        // Use the actual clicked position if it's on a horizontal segment, otherwise use segment midpoint
        const point1 = points[segmentIndex].getPosition();
        const point2 = points[segmentIndex + 1].getPosition();

        // Check if it's a horizontal segment
        if (Math.abs(point1.y - point2.y) < 5) {
          // Use clicked X position on horizontal segment
          midX = this.state.clickedPosition.x;
          midY = point1.y; // Use the segment's Y coordinate
        } else {
          // For non-horizontal segments, use midpoint
          midX = (point1.x + point2.x) / 2;
          midY = (point1.y + point2.y) / 2;
        }
      } else {
        // Fallback to default positioning
        midX = (points[0].getX() + points[1].getX()) / 2;
        midY = points[0].getY();
      }

      const circleRadius = 24;

      const selectedDeviceType = this.props.factory.pidEditStore?.addDeviceMenuType;

      if (selectedDeviceType) {
        // Widget options
        const deviceIcon = getDeviceToRender(
          selectedDeviceType,
          false, // isSelected
          false, // inMaintenance
          true, // renderedOnMap
          false, // isConnected
          this.props.token || { colorIcon: '#ffffff' },
        );

        paths.push(
          <g
            key="device-preview"
            transform={`translate(${midX - 25}, ${midY - 22})`}
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
          >
            {deviceIcon}
          </g>,
        );
      } else {
        // Show existing plus icon
        paths.push(
          <g
            key="add-point-overlay"
            onClick={e => {
              e.stopPropagation();
              this.handleAddDeviceClick(e);
            }}
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
          >
            <ClickableArea cx={midX} cy={midY} r={circleRadius + 4} />
            <AddPointCircle cx={midX} cy={midY} r={circleRadius} color={this.props.color} />
            <path
              className="plus-sign-path"
              d={`M ${midX - 8} ${midY} h 16 M ${midX} ${midY - 8} v 16`}
              stroke="rgb(0, 0, 0)"
              strokeWidth="4"
              strokeLinecap="round"
              fill="rgb(0, 0, 0)"
            />
          </g>,
        );
      }
    }

    this.refPaths = [];
    return <this.G data-default-link-test={this.props.link.getOptions().testName}>{paths}</this.G>;
  }
}
